import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/common";
import { Users, FileText, ClipboardList, BarChart3, FileSpreadsheet, FileDown, Bell } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const COLORS = ["#00427A", "#F2B50C", "#2F8F6B", "#B8933A", "#8C4A27", "#3B6FA0", "#D99E00"];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-4 ${accent}`}><Icon className="h-5 w-5" /></div>
      <p className="font-display text-3xl font-black text-slate-900">{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
}

export default function Reports() {
  const [r, setR] = useState(null);

  useEffect(() => { api.get("/reports").then(({ data }) => setR(data)); }, []);
  if (!r) return null;

  const stamp = () => new Date().toISOString().slice(0, 10);
  const fmtDT = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });
  };
  const sections = [
    ["Opportunities Posted by Buyer", r.opportunities_by_poster, "Buyer"],
    ["Opportunities by Type", r.opportunities_by_type, "Type"],
    ["SMMEs Registered per Month", r.smmes_by_month, "Month"],
    ["SMMEs by District", r.smmes_by_district, "District"],
    ["SMMEs by Town / Village", r.smmes_by_town, "Town"],
    ["SMMEs by Sector", r.smmes_by_sector, "Sector"],
  ];
  const summaryRows = [
    ["Total SMMEs Registered", r.total_smmes],
    ["Total Buyers Registered", r.total_buyers],
    ["Total Opportunities Posted", r.total_opportunities],
    ["Total Responses", r.total_responses],
    ["Total Notifications Sent", r.total_notifications],
    ["My Opportunities", r.my_opportunities],
  ];
  const oppLog = r.opportunities_log || [];

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const sumWs = XLSX.utils.aoa_to_sheet([["Metric", "Value"], ...summaryRows]);
    sumWs["!cols"] = [{ wch: 30 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, sumWs, "Summary");
    sections.forEach(([title, data, label]) => {
      const ws = XLSX.utils.aoa_to_sheet([[label, "Count"], ...(data || []).map((d) => [d.name, d.count])]);
      ws["!cols"] = [{ wch: 32 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
    });
    const logWs = XLSX.utils.aoa_to_sheet([
      ["Title", "Type", "Mine", "Posted By", "Date & Time", "Notifications Sent"],
      ...oppLog.map((o) => [o.title, o.type, o.mine_name, o.posted_by_name, fmtDT(o.created_at), o.notified_count]),
    ]);
    logWs["!cols"] = [{ wch: 30 }, { wch: 10 }, { wch: 18 }, { wch: 20 }, { wch: 22 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, logWs, "Opportunities Log");
    XLSX.writeFile(wb, `assmang-reports-${stamp()}.xlsx`);
  };

  const exportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Assmang SMME Portal — Reports", 14, 16);
    doc.setFontSize(9);
    doc.text(`Exported ${stamp()}`, 14, 22);
    autoTable(doc, {
      startY: 26,
      head: [["Metric", "Value"]],
      body: summaryRows.map(([k, v]) => [k, String(v)]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [0, 66, 122] },
    });
    sections.forEach(([title, data, label]) => {
      doc.setFontSize(11);
      doc.text(title, 14, doc.lastAutoTable.finalY + 10);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 13,
        head: [[label, "Count"]],
        body: (data || []).map((d) => [d.name, String(d.count)]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [0, 66, 122] },
        alternateRowStyles: { fillColor: [245, 247, 250] },
      });
    });
    doc.setFontSize(11);
    doc.text("Opportunities Log (Date & Time)", 14, doc.lastAutoTable.finalY + 10);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 13,
      head: [["Title", "Type", "Mine", "Posted By", "Date & Time", "Notif."]],
      body: oppLog.map((o) => [o.title, o.type, o.mine_name, o.posted_by_name, fmtDT(o.created_at), String(o.notified_count)]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [0, 66, 122] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });
    doc.save(`assmang-reports-${stamp()}.pdf`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader eyebrow="Analytics" title="Reports"
          subtitle="Portal registrations, opportunities and response metrics." />
        <div className="flex gap-2">
          <button data-testid="reports-export-excel" onClick={exportExcel}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#1D6F42] text-white text-sm font-medium hover:bg-[#165a35] transition-colors">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <button data-testid="reports-export-pdf" onClick={exportPdf}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#B3261E] text-white text-sm font-medium hover:bg-[#8f1e18] transition-colors">
            <FileDown className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div data-testid="reports-total-smmes-card"><Stat icon={Users} label="Total SMMEs Registered" value={r.total_smmes} accent="bg-blue-50 text-blue-700" /></div>
        <div data-testid="reports-total-buyers-card"><Stat icon={Users} label="Total Buyers Registered" value={r.total_buyers} accent="bg-indigo-50 text-indigo-700" /></div>
        <div data-testid="reports-total-opps-card"><Stat icon={FileText} label="Total Opportunities" value={r.total_opportunities} accent="bg-[#FEF9E7] text-[#F2B50C]" /></div>
        <Stat icon={ClipboardList} label="Total Responses" value={r.total_responses} accent="bg-emerald-50 text-emerald-700" />
        <div data-testid="reports-total-notifications-card"><Stat icon={Bell} label="Total Notifications Sent" value={r.total_notifications} accent="bg-rose-50 text-rose-700" /></div>
        <Stat icon={BarChart3} label="My Opportunities" value={r.my_opportunities} accent="bg-slate-100 text-slate-700" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white rounded-xl border border-slate-200 p-6 lg:col-span-2">
          <h3 className="font-display font-bold text-slate-900 mb-4">SMMEs Registered per Month</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={r.smmes_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#00427A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">Opportunities Posted by Buyer</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={r.opportunities_by_poster}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#00427A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">Opportunities by Type</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={r.opportunities_by_type} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {r.opportunities_by_type.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">SMMEs by District</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={r.smmes_by_district} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#F2B50C" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">SMME Locations (Town / Village)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(r.smmes_by_town || []).slice(0, 12)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8C4A27" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 lg:col-span-2">
          <h3 className="font-display font-bold text-slate-900 mb-4">SMMEs by Sector</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={r.smmes_by_sector}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#2F8F6B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 mt-6 overflow-x-auto">
        <h3 className="font-display font-bold text-slate-900 px-6 pt-6 pb-4">Opportunities Posted — Date &amp; Time</h3>
        {oppLog.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-slate-400">No opportunities posted yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="reports-opp-log-table">
            <thead className="bg-slate-50 border-y border-slate-200 text-left text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Title</th>
                <th className="px-5 py-3 font-semibold">Type</th>
                <th className="px-5 py-3 font-semibold">Mine</th>
                <th className="px-5 py-3 font-semibold">Posted By</th>
                <th className="px-5 py-3 font-semibold">Date &amp; Time</th>
                <th className="px-5 py-3 font-semibold text-right">Notifications</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {oppLog.map((o, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">{o.title}</td>
                  <td className="px-5 py-3 text-slate-600">{o.type}</td>
                  <td className="px-5 py-3 text-slate-600">{o.mine_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{o.posted_by_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{fmtDT(o.created_at)}</td>
                  <td className="px-5 py-3 text-right font-mono-data">{o.notified_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
